import {
  booleanPointInPolygon,
  destination,
  featureCollection,
  lineString,
  pointToLineDistance,
} from '@turf/turf';
import { Button } from 'reactstrap';
import * as shpwrite from 'shp-write';
import { NONE, sectionTypes } from '../../../constants/fields';
import { feetPerMile } from '../../../constants/units';
import { useFarmName } from '../../../utils/hooks';
import { exportFileName } from '../../../utils/utils';

export const canExportShpLandmarkLines = (section) =>
  section.type === sectionTypes.GRID &&
  !(section.rowOverrides || []).some(
    (override) =>
      override?.inRowSpacing != null &&
      override?.cropName !== NONE &&
      override?.inRowSpacing !== section.inRowSpacing,
  );

export const canExportShpBoundary = (section) =>
  section.type !== sectionTypes.LINE;

// Search for a point outside of the envelope by incrementing the distance
// each time until a point outside of the envelope is found.
const findPointOutsidePolygon = ({
  point,
  polygon,
  bearing,
  increment,
  distanceFt = 0,
}) => {
  const newPoint = destination(point, distanceFt / feetPerMile, bearing, {
    units: 'miles',
  });
  if (booleanPointInPolygon(newPoint, polygon)) {
    return findPointOutsidePolygon({
      point,
      polygon,
      bearing,
      increment,
      distanceFt: distanceFt + increment,
    });
  }
  return newPoint;
};

const generateLandmarkLines = (section) => {
  if (!canExportShpLandmarkLines(section)) return [];

  const {
    points,
    rotation,
    inRowSpacing,
    betweenRowSpacing,
    fieldFeature,
  } = section;

  const landmarkLines = [];
  let pointsQueue = points.features.slice();

  // Generate a landmark line for each point still in the queue.
  while (pointsQueue.length > 0) {
    const point = pointsQueue.shift();
    const startPoint = findPointOutsidePolygon({
      point,
      polygon: fieldFeature,
      bearing: rotation - 90,
      increment: betweenRowSpacing,
    });
    const endPoint = findPointOutsidePolygon({
      point,
      polygon: fieldFeature,
      bearing: rotation + 90,
      increment: betweenRowSpacing,
    });
    const landmarkLine = lineString(
      [startPoint.geometry.coordinates, endPoint.geometry.coordinates],
      { name: `Landmark Line ${landmarkLines.length + 1}` },
    );
    landmarkLines.push(landmarkLine);
    // Filter out points that are on (close to) the landmark line.
    pointsQueue = pointsQueue.filter(
      (p) =>
        pointToLineDistance(p, landmarkLine, {
          units: 'miles',
          method: 'planar',
        }) *
          feetPerMile >
        // Use default value of 0.25 ft tolerance for in-row patterns. This may not work
        // well if pattern crop spacing is much less than 0.5 ft, which should be rare,
        // especially with automated marking.
        (inRowSpacing ?? 1) / 4,
    );
  }

  return landmarkLines;
};

const getBoundary = (section) => {
  if (!canExportShpBoundary) return [];
  return [
    {
      ...section.fieldFeature,
      properties: {
        ...section.fieldFeature.properties,
        name: `${section.name} Boundary`,
      },
    },
  ];
};

const getRows = (section) =>
  (section.rows?.features || [])
    .map((feature, i) => ({
      ...feature,
      properties: {
        ...feature.properties,
        row: i + 1,
        name: `${section.name} Row ${i + 1}`,
      },
    }))
    // Don't export no-crop rows
    .filter((_, i) => (section.rowOverrides || [])[i]?.cropName !== NONE);

const getPoints = (section) =>
  (section.points?.features || []).map((feature, i) => ({
    ...feature,
    properties: {
      ...feature.properties,
      // some points will come in with a row, and some will not
      // if they have one, it's likely 1 less than the displayed row number
      // so, add 1 to it if it exists
      row:
        feature.properties.row != null
          ? +feature.properties.row + 1
          : undefined,
      name: `${section.name} Point ${i + 1}`,
    },
  }));

const ExportShpButton = ({ sections, shpOption, ...buttonProps }) => {
  const farmName = useFarmName();
  const onClick = () => {
    sections.forEach((section, i) => {
      // windows limits full file paths to 260 characters
      // truncate to 200 characters to leave some room for the rest of the path
      const filename = exportFileName(
        [section.name, shpOption, farmName].join(' - '),
      );
      const features =
        shpOption === 'boundary'
          ? getBoundary(section)
          : shpOption === 'rows'
          ? getRows(section)
          : shpOption === 'landmark lines'
          ? generateLandmarkLines(section)
          : getPoints(section);

      const geojson = featureCollection(features);
      // Timeout to prevent simultaneously downloading more than
      // 10 files in 1s (Chrome limitation)
      setTimeout(
        () =>
          shpwrite.download(geojson, {
            file: filename,
            folder: filename,
            types: {
              point: filename,
              polyline: filename,
              polygon: filename,
            },
          }),
        i * 120,
      );
    });
  };

  return <Button onClick={onClick} {...buttonProps} />;
};

export default ExportShpButton;

export const __EXPORTS_FOR_TESTS__ = {
  generateLandmarkLines,
  getRows,
  getPoints,
};
